import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button, Chip, Stack,
  IconButton, CircularProgress, Rating, Checkbox, FormControlLabel, Avatar, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import StarsIcon from '@mui/icons-material/Stars';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { eeFetch, fmtDeadline } from './engagementClient';

// The register + self-rate flow for ONE event. Reused by the auto-popup AND the
// leaderboard side tab, so a missed popup can still be completed later.
// Styled with PORTAL theme tokens (light theme, warning amber ramp) so it reads
// as a first-class portal feature — no hardcoded brand colors.
//   onClose()     — user dismissed without finishing
//   onCompleted() — registered and/or rated (parent should refetch)
const FORMAT_LABEL = { 1: 'Teams · auction', 2: 'Groups', 3: 'Individuals' };

export default function EngagementActionFlow({ event, onClose, onCompleted }) {
  const sports = event?.sports || [];
  const rateOnly = event && !event.needs_registration && event.needs_rating;
  const [step, setStep] = useState(event?.needs_registration ? 'invite' : 'rate');
  const [ratings, setRatings] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!event) return;
    if (event.needs_registration) {
      setStep('invite');
      setRatings({});
    } else {
      const init = {};
      (event.sports || []).forEach((s) => { init[s.sport_id] = { rating: 0, interested: true }; });
      setRatings(init);
      setStep('rate');
    }
  }, [event?.event_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!event) return null;

  const setRating = (id, key, val) =>
    setRatings((r) => ({ ...r, [id]: { rating: 0, interested: true, ...r[id], [key]: val } }));
  const finish = () => { setStep('done'); setTimeout(() => onCompleted && onCompleted(), 1500); };

  const register = async () => {
    setBusy(true);
    try {
      const r = await eeFetch(`/events/${event.event_id}/register`, { method: 'POST' });
      if (!r.ok) return onClose && onClose();
      if (event.needs_rating && sports.length) {
        const init = {};
        sports.forEach((s) => { init[s.sport_id] = { rating: 0, interested: true }; });
        setRatings(init);
        setStep('rate');
      } else {
        finish();
      }
    } catch (e) {
      onClose && onClose();
    } finally {
      setBusy(false);
    }
  };

  const submitRatings = async () => {
    setBusy(true);
    const payload = sports.map((s) => {
      const rr = ratings[s.sport_id] || {};
      return {
        sport_id: s.sport_id,
        self_rating: rr.interested !== false && rr.rating > 0 ? rr.rating : null,
        is_interested: rr.interested === false ? 0 : 1,
      };
    });
    try {
      await eeFetch(`/events/${event.event_id}/ratings`, { method: 'POST', body: JSON.stringify({ ratings: payload }) });
    } catch (e) { /* best-effort */ }
    setBusy(false);
    finish();
  };

  const deadline = fmtDeadline(rateOnly ? event.self_rating_deadline : event.registration_deadline);
  const blurb =
    event.description ||
    `Join ${event.event_name}${sports.length ? ` — ${sports.map((s) => s.sport_name).join(', ')}` : ''}! Register now to take part.`;

  const HeaderIcon = step === 'rate' ? StarsIcon : CelebrationIcon;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="ee-flow-title">
      {/* Portal-native header: white paper, amber accent avatar, theme text. */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, position: 'relative' }}>
        <IconButton size="small" onClick={onClose} aria-label="Maybe later — close"
          sx={{ position: 'absolute', top: 10, right: 10, color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: 'warning.lighter', color: 'warning.darker', width: 44, height: 44 }}>
            <HeaderIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: 'warning.darker', lineHeight: 1.4 }}>
              {step === 'rate' ? (rateOnly ? 'Rate your skills' : "You're in — rate your game") : "You're invited"}
            </Typography>
            <Typography id="ee-flow-title" variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              {event.event_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {FORMAT_LABEL[event.format] || 'Engagement event'}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Divider />

      {step === 'done' && (
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 52 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>{rateOnly ? 'Thanks!' : "You're in!"} 🎉</Typography>
          <Typography variant="body2" color="text.secondary">We'll be in touch with the next steps.</Typography>
        </DialogContent>
      )}

      {step === 'invite' && (
        <>
          <DialogContent sx={{ pt: 2.5 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>{blurb}</Typography>
            {sports.length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {sports.map((s) => (
                  <Chip key={s.sport_id} size="small" label={s.sport_name}
                    sx={{ bgcolor: 'warning.lighter', color: 'warning.darker', fontWeight: 600 }} />
                ))}
              </Stack>
            )}
            {deadline && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">Registration closes {deadline}</Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={onClose} color="inherit">Maybe later</Button>
            <Button variant="contained" color="warning" onClick={register} disabled={busy}
              startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}>
              Count me in
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'rate' && (
        <>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Rate yourself in each game (1–5) so captains know your strengths. Untick anything you won't play.
            </Typography>
            {deadline && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', mb: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">Rating closes {deadline}</Typography>
              </Stack>
            )}
            {sports.map((s) => {
              const rr = ratings[s.sport_id] || { rating: 0, interested: true };
              return (
                <Stack key={s.sport_id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}
                  sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{s.sport_name}</Typography>
                    <FormControlLabel sx={{ m: 0 }}
                      control={<Checkbox size="small" sx={{ p: 0.25, mr: 0.5 }} checked={rr.interested !== false}
                        onChange={(e) => setRating(s.sport_id, 'interested', e.target.checked)}
                        inputProps={{ 'aria-label': `I'll play ${s.sport_name}` }} />}
                      label={<Typography variant="caption" color="text.secondary">{rr.interested !== false ? "I'll play" : 'Not playing'}</Typography>} />
                  </Box>
                  <Rating size="medium" value={rr.rating || 0} disabled={rr.interested === false}
                    onChange={(e, v) => setRating(s.sport_id, 'rating', v || 0)}
                    getLabelText={(v) => `${v} star${v === 1 ? '' : 's'} in ${s.sport_name}`} />
                </Stack>
              );
            })}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={onClose} color="inherit" disabled={busy}>Maybe later</Button>
            <Button variant="contained" color="warning" onClick={submitRatings} disabled={busy}
              startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}>
              Save my ratings
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
